import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Activity, AlertTriangle, CheckCircle2, Cloud, FileText, History, Mail, Play, RefreshCw, RotateCcw,
  ShieldCheck, Settings2, XCircle,
} from "lucide-react";
import api, { formatApiErrorDetail, openServerPdf } from "../../lib/api";
import { itDate, itDateTime } from "../../components/SafeGuardArchive";
import { Button, Card, Field, Input, Select, Spinner } from "../../components/ui-kit";

const SEVERITY = {
  alta: "border-red-200 bg-red-50 text-red-800",
  media: "border-amber-200 bg-amber-50 text-amber-800",
  bassa: "border-slate-200 bg-slate-50 text-slate-700",
};
const DOC_STATUS = { verificato: ["Verificato", "bg-emerald-50 text-emerald-700"], errore: ["Errore", "bg-red-50 text-red-700"], in_corso: ["In corso", "bg-blue-50 text-blue-700"] };
const DRIVE_STATUS = {
  verificato: ["DF Drive ok", "bg-emerald-50 text-emerald-700"], errore: ["DF Drive errore", "bg-red-50 text-red-700"],
  in_attesa: ["DF Drive in coda", "bg-slate-100 text-slate-600"], non_configurato: ["DF Drive in coda", "bg-slate-100 text-slate-600"],
  piva_mancante: ["Senza P.IVA", "bg-amber-50 text-amber-700"],
};
const MAIL_STATUS = {
  inviata: ["Email inviata", "bg-emerald-50 text-emerald-700"], errore: ["Email errore", "bg-red-50 text-red-700"],
  nessun_destinatario: ["Nessun destinatario", "bg-amber-50 text-amber-700"], in_corso: ["Email in invio", "bg-blue-50 text-blue-700"],
};
const KIND = { daily: "Giornaliero", monthly: "Fascicolo mensile" };

function Chip({ map, value, fallback }) {
  const [label, cls] = map[value] || [fallback || value || "—", "bg-slate-100 text-slate-600"];
  return <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`}>{label}</span>;
}

function JobTile({ icon: Icon, title, value, detail, tone = "ok", testid }) {
  const tones = { ok: "text-emerald-600 bg-emerald-50", bad: "text-red-600 bg-red-50", off: "text-slate-500 bg-slate-100" };
  return (
    <Card className="flex items-start gap-3 p-4" data-testid={testid}>
      <span className={`grid h-10 w-10 shrink-0 place-items-center rounded-xl ${tones[tone]}`}><Icon className="h-5 w-5" strokeWidth={1.5} /></span>
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wider text-slate-400">{title}</p>
        <p className="font-heading text-lg font-semibold text-navy">{value}</p>
        {detail && <p className="text-xs text-slate-500">{detail}</p>}
      </div>
    </Card>
  );
}

function DocumentsPanel({ tenant, onChanged }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [docs, setDocs] = useState(null);
  const [regen, setRegen] = useState(null); // {kind, period_key}
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    setDocs(null);
    api.get(`/admin/safeguard/tenants/${tenant.tenant_id}/documents`, { params: { month } })
      .then(({ data }) => setDocs(data.documents)).catch(() => setDocs([]));
  }, [tenant.tenant_id, month]);
  useEffect(load, [load]);

  const submit = async () => {
    if (reason.trim().length < 5) { toast.error("Scrivi il motivo della rigenerazione (almeno 5 caratteri)."); return; }
    if (!window.confirm(`Generare una nuova versione del ${regen.kind === "daily" ? "report del " + itDate(regen.period_key) : "fascicolo " + regen.period_key}? La versione attuale resta archiviata come sostituita e il cliente riceverà l'email di aggiornamento.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/safeguard/tenants/${tenant.tenant_id}/regenerate`, { ...regen, reason: reason.trim() });
      toast.success(data.outcome === "generato" ? `Versione ${data.version} generata e verificata.` : `Esito: ${data.outcome}`);
      setRegen(null); setReason(""); load(); onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const months = [];
  for (let d = new Date(), i = 0; i < 12; i += 1, d.setMonth(d.getMonth() - 1)) {
    d.setDate(1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  const maxDay = tenant.last_documentable_day;

  return (
    <div className="space-y-4" data-testid={`sg-docs-${tenant.tenant_id}`}>
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Mese"><Select value={month} onChange={(e) => setMonth(e.target.value)} aria-label="Mese documenti">{months.map((m) => <option key={m} value={m}>{m.slice(5)}/{m.slice(0, 4)}</option>)}</Select></Field>
        <Button variant="outline" className="px-3" onClick={() => setRegen({ kind: "daily", period_key: maxDay })} data-testid="sg-regen-other"><RotateCcw className="h-4 w-4" />Rigenera un altro giorno</Button>
      </div>

      {regen && (
        <div className="space-y-3 rounded-xl border border-gold/40 bg-blue-50/40 p-4" data-testid="sg-regen-form">
          <p className="text-sm font-medium text-navy">Nuova versione — la precedente resta archiviata e tracciata</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Tipo"><Select value={regen.kind} onChange={(e) => setRegen({ kind: e.target.value, period_key: e.target.value === "daily" ? maxDay : maxDay.slice(0, 7) })}>
              <option value="daily">Report giornaliero</option><option value="monthly">Fascicolo mensile</option></Select></Field>
            <Field label={regen.kind === "daily" ? "Giorno (AAAA-MM-GG)" : "Mese (AAAA-MM)"}><Input value={regen.period_key} onChange={(e) => setRegen({ ...regen, period_key: e.target.value })} data-testid="sg-regen-period" /></Field>
          </div>
          <Field label="Motivo (obbligatorio, finisce nel registro e nel documento)"><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Es. Correzione richiesta dal cliente" data-testid="sg-regen-reason" /></Field>
          <div className="flex flex-wrap gap-2">
            <Button onClick={submit} loading={busy} data-testid="sg-regen-submit"><RotateCcw className="h-4 w-4" />Genera nuova versione</Button>
            <Button variant="ghost" onClick={() => { setRegen(null); setReason(""); }}>Annulla</Button>
          </div>
        </div>
      )}

      {docs === null ? <div className="py-6 text-center"><Spinner /></div> : docs.length === 0 ? (
        <p className="text-sm text-slate-500">Nessun documento in questo mese.</p>
      ) : (
        <ul className="divide-y divide-hairline rounded-xl border border-hairline bg-white">
          {docs.map((doc) => (
            <li key={doc.id} className={`flex flex-wrap items-center gap-x-3 gap-y-2 px-3 py-2.5 ${doc.superseded_by ? "opacity-60" : ""}`}>
              <div className="w-full shrink-0 sm:w-36">
                <p className="text-sm font-medium text-navy">{doc.kind === "daily" ? itDate(doc.period_key) : `${doc.period_key.slice(5)}/${doc.period_key.slice(0, 4)}`}</p>
                <p className="text-[11px] text-slate-400">{KIND[doc.kind]} · v{doc.version}{doc.superseded_by ? " · sostituita" : ""}</p>
              </div>
              <div className="flex w-full min-w-0 flex-wrap items-center gap-1.5 sm:w-auto sm:flex-1">
                <Chip map={DOC_STATUS} value={doc.status} />
                {doc.status === "verificato" && <Chip map={DRIVE_STATUS} value={doc.storage?.dfdrive?.status} />}
                {doc.email && <Chip map={MAIL_STATUS} value={doc.email.status} />}
                {doc.trigger === "rigenerazione_manuale" && <span className="w-full text-[11px] text-slate-500 sm:w-auto">Rigenerato da {doc.requested_by?.name || "consulente"}: {doc.reason}</span>}
                {doc.status === "errore" && <span className="text-[11px] text-red-600">{doc.last_error} · tentativo {doc.attempts}</span>}
              </div>
              {doc.status === "verificato" && <Button variant="ghost" className="px-2" onClick={() => openServerPdf(`/admin/safeguard/tenants/${tenant.tenant_id}/documents/${doc.id}/pdf`)}><FileText className="h-4 w-4" />Apri</Button>}
              {!doc.superseded_by && <Button variant="ghost" className="px-2" onClick={() => setRegen({ kind: doc.kind, period_key: doc.period_key })} data-testid={`sg-regen-${doc.period_key}`}><RotateCcw className="h-4 w-4" />Rigenera</Button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConflictsPanel({ tenant, onChanged }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => {
    api.get(`/admin/safeguard/tenants/${tenant.tenant_id}/conflicts`).then(({ data }) => setRows(data.conflicts)).catch(() => setRows([]));
  }, [tenant.tenant_id]);
  useEffect(load, [load]);
  const resolve = async (row) => {
    const note = window.prompt("Nota di verifica (obbligatoria): cosa hai controllato o deciso con il cliente?");
    if (!note || note.trim().length < 3) return;
    try {
      await api.post(`/admin/safeguard/tenants/${tenant.tenant_id}/conflicts/${row.id}/resolve`, { note: note.trim() });
      toast.success("Conflitto segnato come verificato: resta conservato nello storico.");
      load(); onChanged();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mb-4 space-y-2" data-testid={`sg-conflicts-${tenant.tenant_id}`}>
      <p className="text-sm font-semibold text-navy">Registrazioni offline in conflitto</p>
      <p className="text-xs text-slate-500">Fatte senza connessione su un dispositivo mentre lo stesso controllo veniva registrato su un altro: la registrazione esistente non è stata sostituita.</p>
      <ul className="divide-y divide-hairline rounded-xl border border-hairline bg-white">
        {rows.map((row) => (
          <li key={row.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm">
            <span className="w-24 font-medium text-navy">{itDate(row.reference_date)}</span>
            <span className="min-w-0 flex-1">{row.procedure_name}
              <span className="block text-xs text-slate-500">Offline: {row.user_name} alle {itDateTime(row.device_time)} · già registrato da {row.existing_by || "—"} {row.existing_at ? `(${itDateTime(row.existing_at)})` : ""}</span>
              {row.status === "verificato" && <span className="block text-xs text-emerald-700">Verificato da {row.resolved_by}: {row.resolution_note}</span>}
            </span>
            {row.status === "aperto" ? <Button variant="ghost" className="px-2" onClick={() => resolve(row)} data-testid={`sg-conflict-resolve-${row.id}`}><CheckCircle2 className="h-4 w-4" />Segna come verificato</Button>
              : <Chip map={{ verificato: ["Verificato", "bg-emerald-50 text-emerald-700"] }} value="verificato" />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmailPanel({ tenant }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get(`/admin/safeguard/tenants/${tenant.tenant_id}/email`).then(({ data: d }) => {
      setData(d);
      setForm({ ...d.settings, recipientsText: (d.settings.recipients || []).join("\n") });
    }).catch(() => setData(false));
  }, [tenant.tenant_id]);
  useEffect(load, [load]);
  if (data === null) return <div className="py-6 text-center"><Spinner /></div>;
  if (data === false) return <p className="text-sm text-red-600">Impostazioni email non disponibili.</p>;
  const save = async () => {
    setBusy(true);
    try {
      const recipients = form.recipientsText.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean);
      await api.put(`/admin/safeguard/tenants/${tenant.tenant_id}/email`, {
        enabled: form.enabled, daily: form.daily, weekly: form.weekly, monthly: form.monthly, recipients,
      });
      toast.success("Impostazioni email salvate.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const toggle = (key, label) => (
    <label className="flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg border border-hairline px-3">
      <input type="checkbox" className="h-5 w-5" checked={!!form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.checked })} data-testid={`sg-mail-${key}`} />
      <span className="text-sm">{label}</span>
    </label>
  );
  return (
    <div className="grid gap-5 lg:grid-cols-2" data-testid={`sg-mail-${tenant.tenant_id}`}>
      <div className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          {toggle("enabled", "Invii attivi per questa azienda")}
          {toggle("daily", "Giornaliero")}
          {toggle("weekly", "Settimanale (lunedì)")}
          {toggle("monthly", "Fascicolo mensile")}
        </div>
        <Field label="Destinatari (uno per riga; vuoto = email aziendale e titolari)">
          <textarea className="min-h-[96px] w-full rounded-lg border border-hairline p-3 text-sm" value={form.recipientsText}
            onChange={(e) => setForm({ ...form, recipientsText: e.target.value })} data-testid="sg-mail-recipients" />
        </Field>
        <p className="text-xs text-slate-500">Destinatari effettivi: {data.effective_recipients.length ? data.effective_recipients.join(", ") : <span className="text-red-600">nessuno</span>}. Gli indirizzi PEC sono sempre esclusi.</p>
        <Button onClick={save} loading={busy} data-testid="sg-mail-save"><Mail className="h-4 w-4" />Salva impostazioni email</Button>
      </div>
      <div>
        <p className="mb-2 text-xs uppercase tracking-wider text-slate-400">Ultimi invii</p>
        <ul className="divide-y divide-hairline rounded-xl border border-hairline bg-white text-sm">
          {data.log.length === 0 && <li className="p-3 text-slate-500">Nessun invio registrato.</li>}
          {data.log.slice(0, 15).map((row) => (
            <li key={row.id} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <span className="w-28 text-xs text-slate-500">{row.kind === "daily" ? itDate(row.period_key) : row.period_key}</span>
              <Chip map={MAIL_STATUS} value={row.status} />
              <span className="text-xs text-slate-500">{row.sent_at ? itDateTime(row.sent_at) : row.last_error || ""}</span>
              {row.recipients_masked && <span className="text-[11px] text-slate-400">{row.recipients_masked.join(", ")}</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default function SafeGuardAdmin() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({}); // tenant_id -> "docs" | "mail"
  const [running, setRunning] = useState(false);
  const [retrying, setRetrying] = useState(null);

  const load = useCallback(() => api.get("/admin/safeguard/overview").then(({ data: d }) => setData(d)).catch(() => setData(false)), []);
  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setRunning(true);
    try { await api.post("/admin/safeguard/run-now"); toast.success("Giro completato."); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRunning(false); }
  };
  const retry = async (tid) => {
    setRetrying(tid);
    try { const { data: r } = await api.post(`/admin/safeguard/tenants/${tid}/retry`); toast.success(`Ritentati: ${r.documents} report, ${r.dfdrive} copie, ${r.emails} email.`); await load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setRetrying(null); }
  };

  if (data === false) return <Card className="p-6 text-red-700">Pannello SafeGuard non disponibile. <Button variant="ghost" onClick={load}>Riprova</Button></Card>;
  if (!data) return <div className="grid place-items-center py-20"><Spinner /></div>;
  const { job, tenants, anomalies } = data;
  const cycle = job.last_cycle || {};
  const documented = tenants.filter((t) => t.documented);
  const others = tenants.filter((t) => !t.documented);

  return (
    <div className="space-y-6" data-testid="safeguard-admin">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-slate-400">DF HACCP SafeGuard</p>
          <h1 className="flex items-center gap-2 font-heading text-3xl font-bold text-navy"><ShieldCheck className="h-7 w-7" strokeWidth={1.5} />Archivio e continuità</h1>
          <p className="mt-1 text-sm text-slate-500">Report notturni, copie di sicurezza su DF Drive ed email ai clienti, azienda per azienda.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" />Aggiorna</Button>
          <Button onClick={runNow} loading={running} data-testid="sg-run-now"><Play className="h-4 w-4" />Esegui ora un giro</Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <JobTile icon={Activity} testid="sg-job-process" tone={job.stale ? "bad" : "ok"} title="Processo automatico"
          value={job.stale ? "Non risponde" : job.mode === "worker" ? "Worker attivo" : "Attivo"} detail={`Ultimo segnale: ${itDateTime(job.last_heartbeat_at)}`} />
        <JobTile icon={History} title="Ultimo giro" tone={(cycle.report?.errors || cycle.dfdrive?.errori || cycle.email?.errori) ? "bad" : "ok"}
          value={itDateTime(job.last_run_at)} detail={cycle.report ? `Report generati ${cycle.report.generated} · errori ${cycle.report.errors}` : "—"} />
        <JobTile icon={Cloud} title="DF Drive" tone={job.dfdrive_active ? "ok" : "off"} value={job.dfdrive_active ? "Collegato" : "Spento"}
          detail={job.availability_checked_on ? `Controllo disponibilità ${itDate(job.availability_checked_on)}: ${job.availability_result?.ok ?? 0} ok, ${job.availability_result?.problemi ?? 0} problemi` : "Controllo disponibilità ogni giorno dalle 06:00"} />
        <JobTile icon={Mail} title="Email ai clienti" tone={job.email_active ? "ok" : "off"} value={job.email_active ? "Attive" : "Spente"}
          detail={job.email_active ? `Ultimo giro: ${cycle.email?.inviate ?? 0} inviate, ${cycle.email?.errori ?? 0} errori` : "Interruttore SAFEGUARD_EMAIL_ATTIVO o SMTP"} />
      </div>

      <Card className="p-5 sm:p-6" data-testid="sg-anomalies">
        <h2 className="font-heading text-xl font-semibold">Notifiche e anomalie</h2>
        {anomalies.length === 0 ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4" />Nessuna anomalia: report, copie ed email regolari.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {anomalies.map((a, i) => (
              <li key={i} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${SEVERITY[a.severity]}`}>
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span><strong>{a.name}</strong> — {a.text}</span>
                {a.tenant_id && <button type="button" className="ml-auto shrink-0 text-xs font-medium underline" onClick={() => setOpen({ ...open, [a.tenant_id]: "docs" })}>Dettagli</button>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-0" data-testid="sg-tenants">
        <div className="border-b border-hairline px-5 py-4 sm:px-6"><h2 className="font-heading text-xl font-semibold">Aziende ({documented.length})</h2></div>
        <ul className="divide-y divide-hairline">
          {documented.map((t) => {
            const problems = t.missing_days.length + t.failed + t.dfdrive_errors + t.email.errors + (t.sync_conflicts || 0);
            return (
              <li key={t.tenant_id} className="px-5 py-4 sm:px-6" data-testid={`sg-tenant-${t.tenant_id}`}>
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:flex sm:flex-wrap sm:items-center sm:gap-x-6">
                  <div className="col-span-2 flex items-start justify-between gap-3 sm:min-w-[200px] sm:flex-1">
                    <div className="min-w-0">
                      <Link to={`/admin/tenants/${t.tenant_id}`} className="font-medium text-navy hover:underline">{t.name}</Link>
                      <p className="text-xs text-slate-500">Attivo dal {itDate(t.active_since)}{t.tenant_status === "limited" ? " · contratto limitato" : ""}</p>
                    </div>
                    <span className="shrink-0 sm:hidden">{problems === 0 ? <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-label="Tutto regolare" /> : <XCircle className="h-5 w-5 text-red-600" aria-label="Anomalie" />}</span>
                  </div>
                  <div className="sm:w-36"><p className="text-[11px] uppercase tracking-wider text-slate-400">Archivio fino al</p>
                    <p className={`text-sm font-semibold ${t.missing_days.length ? "text-red-600" : "text-navy"}`}>{itDate(t.last_daily?.period_key)}</p>
                    {t.missing_days.length > 0 && <p className="text-[11px] text-red-600">{t.missing_days.length} mancanti</p>}</div>
                  <div className="sm:w-40"><p className="text-[11px] uppercase tracking-wider text-slate-400">DF Drive</p>
                    <p className="text-sm text-navy">{t.dfdrive.last_copy_at ? itDateTime(t.dfdrive.last_copy_at) : "—"}</p>
                    {t.dfdrive.missing_vat > 0 ? <p className="text-[11px] text-amber-700">Senza P.IVA</p> : t.dfdrive_errors ? <p className="text-[11px] text-red-600">{t.dfdrive_errors} in errore</p> : t.dfdrive.pending ? <p className="text-[11px] text-slate-500">{job.dfdrive_active ? `${t.dfdrive.pending} in coda` : "Collegamento spento"}</p> : null}</div>
                  <div className="col-span-2 sm:col-span-1 sm:w-40"><p className="text-[11px] uppercase tracking-wider text-slate-400">Ultima email</p>
                    <p className="text-sm text-navy">{t.email.last_sent ? itDateTime(t.email.last_sent.sent_at) : "—"}</p>
                    {t.email.errors > 0 && <p className="text-[11px] text-red-600">{t.email.errors} non inviate</p>}
                    {t.email.no_recipient > 0 && <p className="text-[11px] text-amber-700">Nessun destinatario</p>}
                    {t.sync_conflicts > 0 && <p className="text-[11px] text-red-600">{t.sync_conflicts} conflitti offline</p>}</div>
                  <div className="hidden flex-wrap gap-1 sm:flex">
                    {problems === 0 ? <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-label="Tutto regolare" /> : <XCircle className="h-5 w-5 text-red-600" aria-label="Anomalie" />}
                  </div>
                  <div className="col-span-2 flex flex-wrap gap-1.5">
                    <Button variant={open[t.tenant_id] === "docs" ? "primary" : "outline"} className="px-3" onClick={() => setOpen({ ...open, [t.tenant_id]: open[t.tenant_id] === "docs" ? null : "docs" })} data-testid={`sg-open-docs-${t.tenant_id}`}><FileText className="h-4 w-4" />Documenti</Button>
                    <Button variant={open[t.tenant_id] === "mail" ? "primary" : "outline"} className="px-3" onClick={() => setOpen({ ...open, [t.tenant_id]: open[t.tenant_id] === "mail" ? null : "mail" })} data-testid={`sg-open-mail-${t.tenant_id}`}><Settings2 className="h-4 w-4" />Email</Button>
                    {problems > 0 && <Button variant="ghost" className="px-3" loading={retrying === t.tenant_id} onClick={() => retry(t.tenant_id)} data-testid={`sg-retry-${t.tenant_id}`}><RefreshCw className="h-4 w-4" />Riprova adesso</Button>}
                  </div>
                </div>
                {open[t.tenant_id] && (
                  <div className="mt-4 rounded-xl bg-haccp-bg p-4">
                    {open[t.tenant_id] === "docs" ? <><ConflictsPanel tenant={t} onChanged={load} /><DocumentsPanel tenant={t} onChanged={load} /></> : <EmailPanel tenant={t} />}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </Card>

      {others.length > 0 && (
        <p className="text-xs text-slate-500">Non documentate (contratto bloccato o in configurazione): {others.map((t) => t.name).join(", ")}.</p>
      )}
    </div>
  );
}
